import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, BACKEND_URL } from '../../App';
import { coverUrl, COVER_WIDTH } from '../../lib/coverUrl';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { StatusBadge } from '../writer/WriterOverview';
import { Check, X, Eye, Loader2, ExternalLink, Plus, Upload, RefreshCw, Trash2, Image as ImageIcon, Sparkles, Search, Megaphone, Send, History, MessageSquare, UploadCloud } from 'lucide-react';
import ReturnBookToWriterDialog from '../../components/ReturnBookToWriterDialog';
import BookTimeline from '../../components/BookTimeline';

const AF_MONTHS = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];
const formatPublicationDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return `${d.getDate()} ${AF_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const BookManagement = ({ onUpdate }) => {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('proofreading');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [processing, setProcessing] = useState(null);
  const [faultList, setFaultList] = useState(null);
  const [settings, setSettings] = useState({});
  const [uploadingCorrection, setUploadingCorrection] = useState(null);
  const [correctionFile, setCorrectionFile] = useState(null);
  const [publicationPreview, setPublicationPreview] = useState(null);
  const [showPublicationDialog, setShowPublicationDialog] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectBookId, setRejectBookId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [returnBook, setReturnBook] = useState(null);
  const [expandedTimelines, setExpandedTimelines] = useState({});
  const [messageBook, setMessageBook] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageHistory, setMessageHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [replaceCoverDialogOpen, setReplaceCoverDialogOpen] = useState(false);
  const [bookForCoverReplace, setBookForCoverReplace] = useState(null);
  const [newCoverFile, setNewCoverFile] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [regeneratingCover, setRegeneratingCover] = useState(null);
  const [coverOptionsDialogOpen, setCoverOptionsDialogOpen] = useState(false);
  const [generatedCovers, setGeneratedCovers] = useState([]);
  const [bookForCoverOptions, setBookForCoverOptions] = useState(null);
  const [savingCover, setSavingCover] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [replacingOriginalForBook, setReplacingOriginalForBook] = useState(null);
  const replaceFileInputRef = useRef(null);
  const [releasable, setReleasable] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [releasing, setReleasing] = useState(null);
  const [publishedSort, setPublishedSort] = useState(() => {
    try {
      return localStorage.getItem('adminPublishedSort') || 'pubdate_desc';
    } catch (e) {
      return 'pubdate_desc';
    }
  });

  useEffect(() => {
    if (activeTab === 'redo') {
      fetchReleasable();
    } else {
      fetchBooks();
    }
    fetchSettings();
  }, [activeTab]);

  // Re-fetch the Gepubliseer list when the chosen sort changes, and remember it for the session.
  useEffect(() => {
    try {
      localStorage.setItem('adminPublishedSort', publishedSort);
    } catch (e) { /* ignore storage errors */ }
    if (activeTab === 'published') {
      fetchBooks();
    }
  }, [publishedSort]);

  const fetchReleasable = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/releasable-fault-lists`);
      setReleasable(response.data);
    } catch (error) {
      toast.error('Kon nie "Herdoen Benodig"-lys laai nie');
    } finally {
      setLoading(false);
    }
  };

  const openPreview = async (bookId) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const response = await axios.get(`${API}/admin/fault-list-preview/${bookId}`);
      setPreviewData({ ...response.data, book_id: bookId });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie voorskou laai nie');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRelease = async (bookId) => {
    setReleasing(bookId);
    try {
      const response = await axios.post(`${API}/admin/release-fault-list/${bookId}`);
      toast.success(response.data.message || 'Foutelys vrygestel');
      setPreviewOpen(false);
      setPreviewData(null);
      fetchReleasable();
      onUpdate && onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie foutelys vrystel nie');
    } finally {
      setReleasing(null);
    }
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`).then(r => setCurrentUser(r.data)).catch(() => {});
  }, []);

  const fetchBooks = async () => {
    try {
      const statusMap = {
        proofreading: 'Wag vir Proeflees',
        in_progress: 'Besig met Proeflees',
        pending: 'Wag vir Goedkeuring',
        corrections: 'Wag vir Regmaak',
        ready: 'Gereed vir Publikasie',
        published: 'Gepubliseer',
        all: null
      };
      const status = statusMap[activeTab];
      let url = status ? `${API}/admin/books?status=${status}` : `${API}/admin/books`;
      if (activeTab === 'published') {
        url += `${url.includes('?') ? '&' : '?'}sort=${publishedSort}`;
      }
      const response = await axios.get(url);
      if (activeTab === 'published') {
        // Keep the server-side sort (applies to the full list, nulls last).
        setBooks(response.data);
      } else {
        // Sort by created_date descending (newest first)
        const sortedBooks = response.data.sort((a, b) => {
          const dateA = new Date(a.created_date || a.updated_at || 0);
          const dateB = new Date(b.created_date || b.updated_at || 0);
          return dateB - dateA;
        });
        setBooks(sortedBooks);
      }
    } catch (error) {
      toast.error('Kon nie boeke laai nie');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchFaultList = async (bookId) => {
    try {
      const response = await axios.get(`${API}/books/${bookId}/fault-list`);
      setFaultList(response.data);
    } catch (error) {
      toast.error('Kon nie foute lys laai nie');
    }
  };

  const handleApprove = async (bookId) => {
    setProcessing(bookId);
    try {
      await axios.post(`${API}/admin/books/${bookId}/approve`);
      toast.success('Boek goedgekeur!');
      fetchBooks();
      onUpdate && onUpdate();
    } catch (error) {
      toast.error('Kon nie goedkeur nie');
    } finally {
      setProcessing(null);
    }
  };

  const handleRouteToProofreading = async (bookId) => {
    if (!window.confirm("Hierdie boek het geen foutelys nie — stuur dit na 'Wag Proeflees' sodat 'n proefleser dit kan behartig?")) return;
    setProcessing(bookId);
    try {
      await axios.post(`${API}/admin/books/${bookId}/route-to-proofreading`);
      toast.success('Boek na Wag Proeflees gestuur.');
      fetchBooks();
      onUpdate && onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie stuur na proeflees nie.');
    } finally {
      setProcessing(null);
    }
  };

  const openRejectDialog = (bookId) => {
    setRejectBookId(bookId);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Verskaf asseblief \'n rede vir afwysing');
      return;
    }
    
    setProcessing(rejectBookId);
    try {
      await axios.post(`${API}/admin/books/${rejectBookId}/reject`, null, {
        params: { notes: rejectionReason }
      });
      toast.success('Boek teruggestuur');
      setRejectDialogOpen(false);
      setRejectionReason('');
      setRejectBookId(null);
      fetchBooks();
    } catch (error) {
      toast.error('Kon nie terugstuur nie');
    } finally {
      setProcessing(null);
    }
  };

  const handleSkipProofreading = async (bookId) => {
    setProcessing(bookId);
    try {
      await axios.post(`${API}/admin/books/${bookId}/skip-proofreading`);
      toast.success('Proeflees oorgeslaan!');
      fetchBooks();
      onUpdate && onUpdate();
    } catch (error) {
      toast.error('Kon nie oorslaan nie');
    } finally {
      setProcessing(null);
    }
  };

  const handleStartProofreading = async (bookId) => {
    setProcessing(bookId);
    try {
      await axios.post(`${API}/proofreading/select-book/${bookId}`);
      toast.success('Boek toegeken — jy word nou na die proeflees skerm geneem.');
      navigate(`/admin/books/proeflees/${bookId}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Kon nie boek kies vir proeflees nie';
      toast.error(msg);
    } finally {
      setProcessing(null);
    }
  };

  const handleReleaseProofreading = async (bookId, bookName) => {
    if (!window.confirm(`Maak "${bookName}" los van proeflees en stuur terug na Wag Proeflees?`)) return;
    setProcessing(bookId);
    try {
      await axios.post(`${API}/proofreading/release-book/${bookId}`);
      toast.success('Boek losgemaak — gestuur terug na Wag Proeflees.');
      fetchBooks();
      onUpdate && onUpdate();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Kon nie losmaak nie';
      toast.error(msg);
    } finally {
      setProcessing(null);
    }
  };

  const handleResetToProofreading = async (bookId, bookName) => {
    if (!window.confirm(
      `Stuur "${bookName}" terug na Wag Proeflees? Dit verwyder die huidige foutelys en die boek kan deur 'n ander proefleser opgepak word.`
    )) return;
    setProcessing(bookId);
    try {
      await axios.post(`${API}/admin/books/${bookId}/reset-to-proofreading`);
      toast.success('Boek teruggestuur na Wag Proeflees.');
      fetchBooks();
      onUpdate && onUpdate();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Kon nie boek terugstuur nie';
      toast.error(msg);
    } finally {
      setProcessing(null);
    }
  };

  const handleRegenerateEpub = async (bookId) => {
    if (!confirm('Wil jy die EPUB vir hierdie boek regenereer met die nuwe hoofstukdeteksie? Dit sal die bestaande EPUB vervang.')) {
      return;
    }
    
    setProcessing(bookId);
    try {
      const response = await axios.post(`${API}/admin/books/${bookId}/regenerate-epub`);
      toast.success('EPUB suksesvol geregenereer met hoofstukstruktuur!');
      fetchBooks();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kon nie EPUB regenereer nie';
      toast.error(errorMsg);
      console.error('EPUB regeneration error:', error);
    } finally {
      setProcessing(null);
    }
  };

  const openDeleteDialog = (book) => {
    setBookToDelete(book);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const openMessageDialog = async (book) => {
    setMessageBook(book);
    setMessageText('');
    setMessageHistory([]);
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/admin/books/${book.id}/messages`);
      setMessageHistory(r.data?.messages || []);
    } catch {
      setMessageHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const triggerReplaceOriginal = (book) => {
    setReplacingOriginalForBook(book);
    // Reset the input so selecting the same file twice still fires onChange
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.value = '';
      replaceFileInputRef.current.click();
    }
  };

  const handleReplaceOriginalFile = async (event) => {
    const file = event.target.files?.[0];
    const book = replacingOriginalForBook;
    if (!file || !book) {
      setReplacingOriginalForBook(null);
      return;
    }
    const lower = file.name.toLowerCase();
    if (!(lower.endsWith('.pdf') || lower.endsWith('.docx') || lower.endsWith('.epub'))) {
      toast.error('Lêer moet .PDF, .DOCX of .EPUB wees');
      setReplacingOriginalForBook(null);
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      toast.info(`Laai "${file.name}" op...`);
      const r = await axios.post(
        `${API}/admin/books/${book.id}/replace-original-file`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const cloud = r.data?.has_cloudinary ? ' (Cloudinary)' : '';
      toast.success(`Oorspronklike lêer vir "${book.boek_naam}" opgedateer${cloud}`);
      fetchBooks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kon nie oorspronklike lêer vervang nie');
    } finally {
      setReplacingOriginalForBook(null);
      if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async () => {
    if (!messageBook) return;
    const text = messageText.trim();
    if (!text) {
      toast.error('Tik asseblief \'n boodskap');
      return;
    }
    setSendingMessage(true);
    try {
      const r = await axios.post(`${API}/admin/books/${messageBook.id}/message-writer`, {
        message: text,
      });
      const emailLine = r.data?.email_status === 'sent'
        ? ` (E-pos ook gestuur na ${r.data.writer_email})`
        : '';
      toast.success(`Boodskap aan ${r.data?.writer_name || 'skrywer'} gestuur${emailLine}`);
      // Refresh history so the just-sent message appears at the top
      try {
        const h = await axios.get(`${API}/admin/books/${messageBook.id}/messages`);
        setMessageHistory(h.data?.messages || []);
      } catch (refreshErr) {
        console.debug('[message-history-refresh] failed:', refreshErr?.message);
      }
      setMessageText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kon nie boodskap stuur nie');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDeleteBook = async () => {
    if (!bookToDelete) return;
    if (!deleteReason.trim()) {
      toast.error('Verskaf asseblief \'n rede vir die verwydering');
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/books/${bookToDelete.id}`, { data: { reason: deleteReason.trim() } });
      toast.success(`Boek "${bookToDelete.boek_naam}" suksesvol verwyder! Die skrywer is per e-pos in kennis gestel.`);
      setDeleteDialogOpen(false);
      setBookToDelete(null);
      setDeleteReason('');
      fetchBooks();
      onUpdate && onUpdate();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kon nie boek verwyder nie';
      toast.error(errorMsg);
      console.error('Delete book error:', error);
    } finally {
      setDeleting(false);
    }
  };

  const openReplaceCoverDialog = (book) => {
    setBookForCoverReplace(book);
    setNewCoverFile(null);
    setReplaceCoverDialogOpen(true);
  };

  const handleReplaceCover = async () => {
    if (!bookForCoverReplace || !newCoverFile) {
      toast.error('Kies asseblief \'n voorblad lêer');
      return;
    }
    
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('cover', newCoverFile);
      
      const response = await axios.post(`${API}/admin/books/${bookForCoverReplace.id}/replace-cover`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Voorblad suksesvol vervang! Herlaai die bladsy as jy die nuwe beeld nie sien nie.');
      setReplaceCoverDialogOpen(false);
      setBookForCoverReplace(null);
      setNewCoverFile(null);
      
      // Force refresh books data with cache-busting
      await fetchBooks();
      
      // Additional: Clear any cached images by forcing a reload
      const coverImages = document.querySelectorAll(`img[src*="files/cover"]`);
      coverImages.forEach(img => {
        const newSrc = img.src.split('?')[0] + '?t=' + Date.now();
        img.src = newSrc;
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kon nie voorblad vervang nie';
      toast.error(errorMsg);
      console.error('Replace cover error:', error);
    } finally {
      setUploadingCover(false);
    }
  };

  const handleRegenerateCover = async (book) => {
    setRegeneratingCover(book.id);
    setBookForCoverOptions(book);
    
    try {
      const response = await axios.post(`${API}/admin/books/${book.id}/regenerate-cover`);
      setGeneratedCovers(response.data.covers);
      setCoverOptionsDialogOpen(true);
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kon nie voorblaaie genereer nie';
      toast.error(errorMsg);
      console.error('Regenerate cover error:', error);
    } finally {
      setRegeneratingCover(null);
    }
  };

  const handleSaveSelectedCover = async (coverData) => {
    setSavingCover(true);
    try {
      const formData = new FormData();
      formData.append('cover_data', coverData);
      
      await axios.post(`${API}/admin/books/${bookForCoverOptions.id}/save-generated-cover`, formData);
      
      toast.success('Voorblad suksesvol gestoor!');
      setCoverOptionsDialogOpen(false);
      setGeneratedCovers([]);
      setBookForCoverOptions(null);
      fetchBooks();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kon nie voorblad stoor nie';
      toast.error(errorMsg);
      console.error('Save cover error:', error);
    } finally {
      setSavingCover(false);
    }
  };

  const handleUploadCorrection = async (bookId) => {
    if (!correctionFile) {
      toast.error('Kies eers \'n file');
      return;
    }

    setUploadingCorrection(bookId);
    const formData = new FormData();
    formData.append('corrected_file', correctionFile);

    try {
      await axios.post(`${API}/books/${bookId}/upload-correction`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('✅ Regstellings suksesvol opgelaai! Boek is nou gereed vir goedkeuring.', {
        duration: 5000
      });
      setCorrectionFile(null);
      setUploadingCorrection(null);
      // Refresh the books list to show updated status
      setTimeout(() => {
        fetchBooks();
        onUpdate && onUpdate();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Oplaai het misluk');
      setUploadingCorrection(null);
    }
  };

  const handlePublish = async (bookId) => {
    const readingWebsiteUrl = settings.reading_website_url || 'https://biblioteek.louisscheepers.com/admin/books/create';
    
    try {
      // Download publication package - use form submission for reliable download
      const token = localStorage.getItem('token');
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = `${API}/files/publication-package/${bookId}`;
      form.target = '_blank';
      form.style.display = 'none';
      // Add auth token as hidden field
      const tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = 'token';
      tokenInput.value = token;
      form.appendChild(tokenInput);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      
      toast.success('Publikasie pakket afgelaai!', { duration: 3000 });
      
      // Open reading website in new tab
      setTimeout(() => {
        window.open(readingWebsiteUrl, '_blank');
      }, 500);
      
      // Mark as published
      await axios.post(`${API}/admin/books/${bookId}/publish`);
      toast.success('Boek gemerk as gepubliseer!');
      fetchBooks();
    } catch (error) {
      toast.error('Kon nie publiseer nie');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Boeke Bestuur</h2>
          <p className="text-gray-600">Bestuur alle boeke in die stelsel</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="add-book-btn"
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
            onClick={() => navigate('/admin/books/add')}
          >
            <Plus className="h-5 w-5 mr-2" />
            Voeg Boek By
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setLoading(true); setSearchQuery(''); setSelectedIds([]); }}>
        <TabsList className="flex w-full flex-wrap h-auto gap-1">
          <TabsTrigger value="proofreading" data-testid="tab-proofreading">Wag Proeflees</TabsTrigger>
          <TabsTrigger value="in_progress" data-testid="tab-in-progress">Besig met Proeflees</TabsTrigger>
          <TabsTrigger value="pending">Wag Goedkeuring</TabsTrigger>
          <TabsTrigger value="corrections">Wag Regstellings</TabsTrigger>
          <TabsTrigger value="ready">Gereed Publikasie</TabsTrigger>
          <TabsTrigger value="published">Gepubliseer</TabsTrigger>
          <TabsTrigger value="redo" data-testid="tab-redo">Herdoen Benodig</TabsTrigger>
          <TabsTrigger value="all">Alles</TabsTrigger>
        </TabsList>

        {/* Search bar */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Soek boek op naam..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 w-full md:w-96"
            data-testid="book-search-input"
          />
        </div>

        <TabsContent value={activeTab} className="space-y-4 mt-6">
          {activeTab === 'redo' && (
            <div className="space-y-4" data-testid="redo-list">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                Hierdie boeke het meer as die maksimum foute gehad en het net 'n "herdoen"-kennisgewing ontvang — die skrywer het nooit die foutelys gekry nie. Gebruik <strong>Voorskou</strong> om die presiese e-pos en PDF te sien, en <strong>Vrystel</strong> om die volledige foutelys aan die skrywer te stuur (geen koste). Die boek skuif dan na "Wag Regstellings".
              </div>
              {loading ? (
                <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
              ) : releasable.length === 0 ? (
                <Card><CardContent className="py-12 text-center">
                  <p className="text-gray-500">Geen boeke wag om vrygestel te word nie.</p>
                </CardContent></Card>
              ) : (
                releasable.map((b) => (
                  <Card key={b.id} data-testid={`redo-row-${b.id}`}>
                    <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
                      <div className="min-w-[240px]">
                        <h3 className="text-lg font-bold text-gray-900">{b.boek_naam}</h3>
                        <p className="text-gray-600 text-sm">deur {b.skrywer_naam || 'Onbekend'}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {b.fault_count_submitted ? `${b.fault_count_submitted} foute gevind` : `${b.stored_entries} foute`}
                          {b.fault_count_submitted && b.stored_entries < b.fault_count_submitted
                            ? ` · ${b.stored_entries} in gestoorde lys`
                            : ''} · {b.date}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm" variant="outline"
                          className="border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                          onClick={() => openPreview(b.id)}
                          disabled={!b.has_fault_list}
                          data-testid={`preview-fault-list-${b.id}`}
                          title={b.has_fault_list ? 'Sien die e-pos en PDF voorskou' : 'Geen foutelys beskikbaar nie'}
                        >
                          <Eye className="h-4 w-4 mr-2" />Voorskou
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => handleRelease(b.id)}
                          disabled={!b.has_fault_list || releasing === b.id}
                          data-testid={`release-fault-list-${b.id}`}
                          title="Stuur die volledige foutelys aan die skrywer"
                        >
                          {releasing === b.id
                            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig...</>
                            : <><Send className="h-4 w-4 mr-2" />Vrystel</>}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
          {activeTab !== 'redo' && (activeTab === 'pending' || activeTab === 'ready') && selectedIds.length > 0 && (
            <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg p-3"
                 data-testid="bulk-action-bar">
              <span className="font-semibold text-blue-900">{selectedIds.length} boek(e) geselekteer</span>
              <Button size="sm" variant="outline" onClick={() => setSelectedIds([])}>Maak skoon</Button>
              <Button
                size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={bulkRunning}
                onClick={async () => {
                  if (!window.confirm(`Goedkeur ${selectedIds.length} boeke?`)) return;
                  setBulkRunning(true);
                  try {
                    const r = await axios.post(`${API}/admin/books/bulk-action`, { book_ids: selectedIds, action: 'approve' });
                    toast.success(`${r.data.affected} boek(e) goedgekeur`);
                    setSelectedIds([]); fetchBooks(); onUpdate && onUpdate();
                  } catch (e) { toast.error('Bulk goedkeuring het misluk'); }
                  finally { setBulkRunning(false); }
                }}
                data-testid="bulk-approve-btn"
              >Goedkeur Alles</Button>
              <Button
                size="sm" variant="outline" className="border-red-500 text-red-600 hover:bg-red-50"
                disabled={bulkRunning}
                onClick={async () => {
                  const reason = window.prompt(`Verwerp ${selectedIds.length} boeke. Rede (opsioneel):`, '');
                  if (reason === null) return;
                  setBulkRunning(true);
                  try {
                    const r = await axios.post(`${API}/admin/books/bulk-action`, { book_ids: selectedIds, action: 'reject', reason });
                    toast.success(`${r.data.affected} boek(e) teruggestuur`);
                    setSelectedIds([]); fetchBooks(); onUpdate && onUpdate();
                  } catch (e) { toast.error('Bulk verwerping het misluk'); }
                  finally { setBulkRunning(false); }
                }}
                data-testid="bulk-reject-btn"
              >Verwerp Alles</Button>
            </div>
          )}
          {activeTab === 'published' && (
            <div className="flex items-center justify-end gap-2" data-testid="published-sort-toolbar">
              <label htmlFor="published-sort" className="text-sm text-gray-600">Sorteer:</label>
              <select
                id="published-sort"
                data-testid="published-sort-select"
                value={publishedSort}
                onChange={(e) => setPublishedSort(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400"
              >
                <option value="pubdate_desc">Nuutste eerste</option>
                <option value="pubdate_asc">Oudste eerste</option>
                <option value="title_asc">Titel A–Z</option>
                <option value="title_desc">Titel Z–A</option>
              </select>
            </div>
          )}

          {activeTab !== 'redo' && (() => {
            // Filter books by search query
            const filteredBooks = books.filter(book => 
              book.boek_naam?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              book.skrywer_naam?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            
            if (filteredBooks.length === 0) {
              return (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-gray-500">
                      {searchQuery ? `Geen boeke gevind vir "${searchQuery}"` : 'Geen boeke nie'}
                    </p>
                  </CardContent>
                </Card>
              );
            }
            
            return filteredBooks.map((book) => (
              <Card key={book.id}>
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    {(activeTab === 'pending' || activeTab === 'ready') && (
                      <input
                        type="checkbox"
                        className="mt-2 h-5 w-5 accent-blue-600 self-start"
                        checked={selectedIds.includes(book.id)}
                        onChange={() => setSelectedIds(prev =>
                          prev.includes(book.id) ? prev.filter(i => i !== book.id) : [...prev, book.id]
                        )}
                        data-testid={`bulk-select-${book.id}`}
                        aria-label={`Selekteer ${book.boek_naam}`}
                      />
                    )}
                    <img 
                      key={`cover-${book.id}-${book.cover_path}`}
                      src={coverUrl(book.id, COVER_WIDTH.list, new Date(book.updated_at || book.created_date || Date.now()).getTime())} 
                      alt={book.boek_naam} 
                      loading="lazy"
                      width={96}
                      height={144}
                      className="w-24 h-36 object-cover rounded shadow" 
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{book.boek_naam}</h3>
                          <p className="text-gray-600">deur {book.skrywer_naam}</p>
                          {book.status === 'Gepubliseer' && formatPublicationDate(book.publication_date) && (
                            <p className="text-sm text-gray-500 mt-1" data-testid="book-publication-date">
                              Gepubliseer: {formatPublicationDate(book.publication_date)}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                          <StatusBadge status={book.status} />
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            book.book_type === 'Oorspronklik' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {book.book_type || 'Vertaling'}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                        <div><p className="text-gray-500">Genre</p><p className="font-semibold">{Array.isArray(book.genre) ? book.genre.join(', ') : book.genre}</p></div>
                        <div><p className="text-gray-500">Woorde</p><p className="font-semibold">{book.word_count?.toLocaleString()}</p></div>
                        <div><p className="text-gray-500">Bladsye</p><p className="font-semibold">{book.page_count}</p></div>
                        <div><p className="text-gray-500">Muntstukke</p><p className="font-semibold">{book.coin_value}</p></div>
                      </div>

                      {/* Compact status timeline — collapsible */}
                      <div className="mb-3">
                        <button
                          type="button"
                          onClick={() => setExpandedTimelines(prev => ({ ...prev, [book.id]: !prev[book.id] }))}
                          className="text-xs text-gray-500 hover:text-amber-600 inline-flex items-center gap-1"
                          data-testid={`toggle-timeline-${book.id}`}
                        >
                          <History className="h-3 w-3" />
                          {expandedTimelines[book.id] ? 'Versteek tydlyn' : 'Wys tydlyn'}
                        </button>
                        {expandedTimelines[book.id] && (
                          <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200 overflow-x-auto">
                            <BookTimeline bookId={book.id} apiBase={API} />
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-sky-700 border-sky-300 hover:bg-sky-50"
                          onClick={() => openMessageDialog(book)}
                          data-testid={`message-writer-btn-${book.id}`}
                          title={`Stuur 'n boodskap aan ${book.skrywer_naam}`}
                        >
                          <MessageSquare className="h-4 w-4 mr-1" /> Boodskap
                          {book.writer_messages?.length > 0 && (
                            <span className="ml-1.5 inline-flex items-center justify-center bg-sky-600 text-white rounded-full text-[10px] font-bold w-4 h-4">
                              {book.writer_messages.length}
                            </span>
                          )}
                        </Button>
                        {currentUser?.is_main_admin && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-violet-700 border-violet-300 hover:bg-violet-50"
                            onClick={() => triggerReplaceOriginal(book)}
                            data-testid={`replace-original-btn-${book.id}`}
                            title="Laai die oorspronklike PDF/DOCX/EPUB weer op (Hoof Admin)"
                          >
                            <UploadCloud className="h-4 w-4 mr-1" /> Laai oorspronklike op
                          </Button>
                        )}
                        {/* For books waiting for corrections - show upload option */}
                        {book.status === 'Wag vir Regmaak' && !book.corrected_file_path && (
                          <div className="w-full space-y-3">
                            <div className="p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                              <p className="text-sm text-yellow-900 font-semibold mb-1">
                                ⏳ Wag vir regstellings
                              </p>
                              <p className="text-xs text-yellow-700">
                                Laai die gekorrigeerde weergawe van die boek op om voort te gaan na goedkeuring
                              </p>
                            </div>

                            {currentUser?.is_main_admin && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-orange-500 text-orange-700 hover:bg-orange-50"
                                onClick={() => handleResetToProofreading(book.id, book.boek_naam)}
                                disabled={processing === book.id}
                                data-testid={`reset-to-proofread-${book.id}`}
                                title="Verwyder die huidige foutelys en stuur die boek terug na Wag Proeflees vir nuwe nasiening"
                              >
                                {processing === book.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>Maak Los — Stuur na Wag Proeflees</>
                                )}
                              </Button>
                            )}
                            
                            <div className="flex items-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-lg">
                              <input
                                type="file"
                                accept=".pdf,.docx,.epub,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/epub+zip"
                                onChange={(e) => setCorrectionFile(e.target.files[0])}
                                className="flex-1 text-sm"
                                id={`correction-file-${book.id}`}
                              />
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600 text-white px-6"
                                onClick={() => handleUploadCorrection(book.id)}
                                disabled={!correctionFile || uploadingCorrection === book.id}
                              >
                                {uploadingCorrection === book.id ? (
                                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig met oplaai...</>
                                ) : (
                                  <><Upload className="h-4 w-4 mr-2" />Laai Regstellings Op</>
                                )}
                              </Button>
                            </div>
                            {correctionFile && (
                              <p className="text-xs text-green-600 font-semibold">
                                ✓ File gekies: {correctionFile.name}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Show success message if in approval stage after upload */}
                        {book.status === 'Wag vir Goedkeuring' && book.corrected_file_path && (
                          <div className="w-full p-4 bg-green-50 border-2 border-green-300 rounded-lg">
                            <p className="text-sm text-green-900 font-semibold mb-1">
                              ✅ Regstellings opgelaai
                            </p>
                            <p className="text-xs text-green-700">
                              Boek is nou gereed vir finale goedkeuring. Kyk die regstellings na en keur goed of stuur terug.
                            </p>
                          </div>
                        )}

                        {/* Proofreader marked the book as having NO faults — skipped fault list entirely */}
                        {book.status === 'Wag vir Goedkeuring' && book.no_faults_found && (
                          <div className="w-full p-4 bg-emerald-50 border-2 border-emerald-300 rounded-lg">
                            <p className="text-sm text-emerald-900 font-semibold mb-1">
                              ✅ Geen foute gevind deur proefleser
                            </p>
                            <p className="text-xs text-emerald-700">
                              Die proefleser het hierdie boek as foutvry gemerk. Kyk dit gerus self na en keur goed om dadelik te publiseer.
                            </p>
                          </div>
                        )}

                        {/* View the book in the in-app reader — available at any stage */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                          onClick={() => navigate(`/admin/books/proeflees/${book.id}`, { state: { boekNaam: book.boek_naam } })}
                          data-testid={`view-book-${book.id}`}
                          title="Sien die boek in die leser (soos tydens proeflees)"
                        >
                          <Eye className="h-4 w-4 mr-2" />Sien Boek
                        </Button>

                        {/* Delete book — available at any phase (Main Admin + Handler). Backend
                            blocks handlers from deleting already-published books. */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-400 text-red-600 hover:bg-red-50"
                          onClick={() => openDeleteDialog(book)}
                          data-testid={`delete-book-${book.id}`}
                          title="Verwyder boek permanent"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />Verwyder
                        </Button>

                        {/* Compare original vs corrected — approval view, once a correction exists */}
                        {book.status === 'Wag vir Goedkeuring' && book.corrected_file_path && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-teal-500 text-teal-700 hover:bg-teal-50"
                            onClick={() => navigate(`/admin/books/compare/${book.id}`)}
                            data-testid={`compare-book-${book.id}`}
                            title="Sien die oorspronklike en gekorrigeerde weergawe langs mekaar"
                          >
                            <Eye className="h-4 w-4 mr-2" />Vergelyk Weergawes
                          </Button>
                        )}

                        {(book.status === 'Wag vir Goedkeuring' || book.status === 'Wag vir Regmaak') && !book.no_faults_found && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button size="sm" variant="outline" onClick={() => fetchFaultList(book.id)}>
                                <Eye className="h-4 w-4 mr-2" />Foute Lys
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Foute Lys</DialogTitle>
                              </DialogHeader>
                              {faultList?.fault_entries ? (
                                <div className="space-y-4">
                                  {faultList.fault_entries.map((entry, index) => (
                                    <div key={`fault-${entry.bladsy_paragraaf || ''}-${index}`} className="p-4 bg-orange-50 rounded-lg space-y-2">
                                      <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div><strong>Bladsy/Paragraaf:</strong> {entry.bladsy_paragraaf}</div>
                                        <div><strong>Tipe:</strong> {entry.fout_tipe}</div>
                                        <div className="col-span-2"><strong>Oorspronklik:</strong> <span className="text-red-600">{entry.oorspronklike_teks}</span></div>
                                        <div className="col-span-2"><strong>Regstelling:</strong> <span className="text-green-600">{entry.voorgestelde_regstelling}</span></div>
                                        <div className="col-span-2"><strong>Nota:</strong> {entry.nota}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-gray-500">Geen foute lys nie</p>
                              )}
                            </DialogContent>
                          </Dialog>
                        )}

                        {book.status === 'Wag vir Goedkeuring' && (
                          <>
                            {!book.fault_list_id && !book.no_faults_found && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-500 text-blue-700 hover:bg-blue-50"
                                onClick={() => handleRouteToProofreading(book.id)}
                                disabled={processing === book.id}
                                data-testid={`route-to-proofreading-${book.id}`}
                                title="Hierdie boek het nog geen foutelys nie — stuur na Wag Proeflees vir 'n proefleser om dit te behartig"
                              >
                                {processing === book.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Stuur na Proeflees</>}
                              </Button>
                            )}
                            <Button
                              data-testid="approve-book-btn"
                              size="sm"
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => handleApprove(book.id)}
                              disabled={processing === book.id}
                            >
                              {processing === book.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-2" />Goedkeur</>}
                            </Button>

                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => openRejectDialog(book.id)}
                              disabled={processing === book.id}
                            >
                              <X className="h-4 w-4 mr-2" />Stuur Terug
                            </Button>
                          </>
                        )}

                        {book.status === 'Wag vir Proeflees' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-orange-500 hover:bg-orange-600"
                              onClick={() => handleSkipProofreading(book.id)}
                              disabled={processing === book.id}
                            >
                              Slaan Proeflees Oor
                            </Button>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleStartProofreading(book.id)}
                              disabled={processing === book.id}
                              data-testid={`start-proofread-${book.id}`}
                              title="Begin self proeflees - laai die EPUB af, lys foute, en stuur na die skrywer vir regstellings"
                            >
                              {processing === book.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Begin Proeflees'
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-amber-500 text-amber-700 hover:bg-amber-50"
                              onClick={() => setReturnBook(book)}
                              data-testid={`return-book-btn-${book.id}`}
                              title="Stuur die boek terug na die skrywer met 'n rede (bv. herlaai in PDF/DOCX/EPUB)"
                            >
                              <Send className="h-4 w-4 mr-2" />
                              Stuur Terug na Skrywer
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-500 text-blue-600 hover:bg-blue-50"
                              onClick={() => handleRegenerateEpub(book.id)}
                              disabled={processing === book.id}
                              title="Regenereer EPUB met nuwe hoofstukdeteksie"
                            >
                              {processing === book.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Regenereer EPUB
                                </>
                              )}
                            </Button>
                          </>
                        )}

                        {book.status === 'Besig met Proeflees' && (
                          <>
                            {book.proofreader_id === currentUser?.id ? (
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => navigate(`/admin/books/proeflees/${book.id}`)}
                                data-testid={`resume-proofread-${book.id}`}
                                title="Hervat proeflees vir hierdie boek"
                              >
                                Hervat Proeflees
                              </Button>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-violet-50 border border-violet-200 text-violet-700"
                                data-testid={`proofreader-badge-${book.id}`}
                                title="Hierdie boek word deur 'n ander proefleser hanteer"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                Word geproeflees deur {book.proofreader_naam || 'ander proefleser'}
                              </span>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-amber-500 text-amber-700 hover:bg-amber-50"
                              onClick={() => setReturnBook(book)}
                              data-testid={`return-book-btn-${book.id}`}
                              title="Stuur boek terug na skrywer (release claim + rede)"
                            >
                              <Send className="h-4 w-4 mr-2" />
                              Stuur Terug na Skrywer
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-orange-500 text-orange-700 hover:bg-orange-50"
                              onClick={() => handleReleaseProofreading(book.id, book.boek_naam)}
                              disabled={processing === book.id}
                              data-testid={`release-proofread-${book.id}`}
                              title="Stuur boek terug na 'Wag Proeflees' (verwyder huidige proeflees toewysing)"
                            >
                              {processing === book.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Maak los'}
                            </Button>
                          </>
                        )}

                        {(book.status === 'Gereed vir Publikasie' || book.status === 'Gepubliseer') && (
                          <>
                            <Button
                              data-testid="preview-book-btn"
                              size="sm"
                              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                              onClick={() => navigate(`/admin/publication-preview/${book.id}`)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              {book.status === 'Gepubliseer' ? 'Bekyk Publikasie' : 'Voorskou & Publiseer'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-500 text-blue-600 hover:bg-blue-50"
                              onClick={() => handleRegenerateEpub(book.id)}
                              disabled={processing === book.id}
                              title="Regenereer EPUB met nuwe hoofstukdeteksie"
                            >
                              {processing === book.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Regenereer EPUB
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-orange-500 text-orange-600 hover:bg-orange-50"
                              onClick={() => navigate(`/admin/adverteer?book_id=${book.id}`)}
                              data-testid={`create-advert-${book.id}`}
                              title="Skep dadelik 'n advert vir hierdie boek (voorblad en byskrif word outomaties ingevul)"
                            >
                              <Megaphone className="h-4 w-4 mr-2" />
                              Skep Advert
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-purple-500 text-purple-600 hover:bg-purple-50"
                              onClick={() => openReplaceCoverDialog(book)}
                              title="Herlaai voorblad (BACKUP PLAN)"
                            >
                              <ImageIcon className="h-4 w-4 mr-2" />
                              Herlaai Voorblad
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ));
          })()}
        </TabsContent>
      </Tabs>

      {/* Rejection Reason Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rede vir Terugstuur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Verskaf asseblief 'n rede hoekom die boek teruggestuur word. Die skrywer sal hierdie rede sien.
            </p>
            <Textarea
              placeholder="Bv: Die boek bevat steeds spelfoute in hoofstuk 3. Kontak asseblief vir meer inligting."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={5}
              className="w-full"
            />
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setRejectDialogOpen(false);
                  setRejectionReason('');
                  setRejectBookId(null);
                }}
                disabled={processing === rejectBookId}
              >
                Kanselleer
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={processing === rejectBookId || !rejectionReason.trim()}
              >
                {processing === rejectBookId ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <X className="h-4 w-4 mr-2" />
                )}
                Stuur Terug
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cover Options Dialog */}
      <Dialog open={coverOptionsDialogOpen} onOpenChange={setCoverOptionsDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Sparkles className="h-5 w-5" />
              Kies AI Voorblad
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Kies die voorblad wat jy wil gebruik vir <strong>{bookForCoverOptions?.boek_naam}</strong>
            </p>
            <div className="grid grid-cols-3 gap-4">
              {generatedCovers.map((cover) => (
                <div key={cover.id} className="space-y-2">
                  <div className="border-2 border-gray-200 rounded-lg overflow-hidden hover:border-green-500 transition-colors">
                    <img 
                      src={`data:image/png;base64,${cover.image_base64}`}
                      alt={`Opsie ${cover.id + 1}`}
                      className="w-full h-auto"
                    />
                  </div>
                  <Button
                    size="sm"
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => handleSaveSelectedCover(cover.image_base64)}
                    disabled={savingCover}
                  >
                    {savingCover ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      `Gebruik Opsie ${cover.id + 1}`
                    )}
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setCoverOptionsDialogOpen(false);
                  setGeneratedCovers([]);
                  setBookForCoverOptions(null);
                }}
                disabled={savingCover}
              >
                Kanselleer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Replace Cover Dialog */}
      <Dialog open={replaceCoverDialogOpen} onOpenChange={setReplaceCoverDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <ImageIcon className="h-5 w-5" />
              Vervang Voorblad
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Vervang die voorblad vir <strong>{bookForCoverReplace?.boek_naam}</strong>
            </p>
            <div>
              <Label htmlFor="cover-upload">Kies Nuwe Voorblad</Label>
              <Input
                id="cover-upload"
                type="file"
                accept="image/*"
                onChange={(e) => setNewCoverFile(e.target.files[0])}
                disabled={uploadingCover}
              />
              <p className="text-xs text-gray-500 mt-1">
                Aanvaarbare formate: JPG, PNG, WEBP
              </p>
              {newCoverFile && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ {newCoverFile.name}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setReplaceCoverDialogOpen(false);
                  setBookForCoverReplace(null);
                  setNewCoverFile(null);
                }}
                disabled={uploadingCover}
              >
                Kanselleer
              </Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={handleReplaceCover}
                disabled={uploadingCover || !newCoverFile}
              >
                {uploadingCover ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Besig...
                  </>
                ) : (
                  <>
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Vervang Voorblad
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Book Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Bevestig Verwydering
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Is jy seker jy wil <strong>{bookToDelete?.boek_naam}</strong> permanent verwyder?
            </p>
            <div className="bg-red-50 p-3 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">
                <strong>⚠️ Waarskuwing:</strong> Hierdie aksie kan NIE ongedaan gemaak word nie. Die boek, alle lêers, en alle verwante data sal permanent verwyder word.
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Rede vir verwydering <span className="text-red-600">*</span>
              </label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Bv. Duplikaat inskrywing, skrywer het versoek om te verwyder, ens."
                rows={3}
                data-testid="delete-book-reason-input"
                disabled={deleting}
              />
              <p className="text-xs text-gray-500">
                Die skrywer sal hierdie rede per e-pos ontvang.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setBookToDelete(null);
                  setDeleteReason('');
                }}
                disabled={deleting}
              >
                Kanselleer
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteBook}
                disabled={deleting || !deleteReason.trim()}
                data-testid="confirm-delete-book-button"
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Besig...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Verwyder
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fault-list release preview (email + PDF) */}
      <Dialog open={previewOpen} onOpenChange={(o) => { setPreviewOpen(o); if (!o) setPreviewData(null); }}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="fault-list-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-indigo-600" />
              Voorskou: Foutelys-e-pos
            </DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
          ) : previewData ? (
            <div className="space-y-4">
              <div className="text-sm space-y-1 bg-gray-50 border rounded-lg p-3">
                <p><span className="text-gray-500">Aan:</span> <strong data-testid="preview-writer-email">{previewData.writer_email || 'Geen e-pos'}</strong> {previewData.writer_name ? `(${previewData.writer_name})` : ''}</p>
                <p><span className="text-gray-500">Onderwerp:</span> <strong>{previewData.subject}</strong></p>
                <p><span className="text-gray-500">Foute in lys:</span> {previewData.stored_entries}{previewData.fault_count !== previewData.stored_entries ? ` (oorspronklik ${previewData.fault_count} gevind)` : ''}</p>
              </div>
              {previewData.fault_count !== previewData.stored_entries && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                  Let wel: hierdie boek se oorspronklike foutelys is afgekap tydens die "herdoen"-proses. Slegs {previewData.stored_entries} foute is gestoor en sal aangeheg word.
                </div>
              )}
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">E-POS VOORSKOU</p>
                <iframe
                  title="E-pos voorskou"
                  srcDoc={previewData.html}
                  className="w-full h-[360px] border rounded-lg bg-white"
                  data-testid="preview-email-iframe"
                />
              </div>
              <div className="flex flex-wrap justify-between gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => window.open(`${BACKEND_URL}${previewData.pdf_url}`, '_blank')}
                  data-testid="preview-open-pdf-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />Sien PDF-aanhangsel
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => handleRelease(previewData.book_id)}
                  disabled={releasing === previewData.book_id || !previewData.writer_email}
                  data-testid="preview-release-btn"
                >
                  {releasing === previewData.book_id
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig...</>
                    : <><Send className="h-4 w-4 mr-2" />Vrystel na Skrywer</>}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>


      <ReturnBookToWriterDialog
        open={!!returnBook}
        book={returnBook}
        onClose={() => setReturnBook(null)}
        onSent={() => fetchBooks()}
      />

      {/* Hidden file input used by the "Laai oorspronklike op" button on each book card */}
      <input
        ref={replaceFileInputRef}
        type="file"
        accept=".pdf,.docx,.epub"
        className="hidden"
        onChange={handleReplaceOriginalFile}
        data-testid="replace-original-file-input"
      />

      <Dialog open={!!messageBook} onOpenChange={(o) => { if (!o) { setMessageBook(null); setMessageText(''); setMessageHistory([]); } }}>
        <DialogContent data-testid="message-writer-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Stuur boodskap aan {messageBook?.skrywer_naam || 'skrywer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Boek: <strong>{messageBook?.boek_naam}</strong>. Die skrywer kry &apos;n
              kennisgewing in die app en (indien e-pos opgestel is) &apos;n e-pos.
              Boek-status verander nie.
            </p>

            {/* Message history — newest first */}
            <div className="border rounded-md bg-gray-50 max-h-56 overflow-y-auto" data-testid="message-history-list">
              {loadingHistory ? (
                <div className="p-3 text-xs text-gray-500 flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Laai geskiedenis...
                </div>
              ) : messageHistory.length === 0 ? (
                <div className="p-3 text-xs text-gray-400 italic">
                  Geen vorige boodskappe aan hierdie skrywer oor hierdie boek nie.
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {messageHistory.map((m, i) => {
                    const when = m.at ? new Date(m.at) : null;
                    const whenStr = when ? when.toLocaleString('af-ZA', {
                      year: 'numeric', month: 'short', day: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    }) : '';
                    const emailOk = m.email_status === 'sent';
                    return (
                      <li key={`${m.at}-${i}`} className="p-2.5 text-xs" data-testid={`message-history-item-${i}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-semibold text-gray-700">{m.from_name || 'Admin'}</span>
                          <span className="text-gray-400">{whenStr}</span>
                        </div>
                        <p className="text-gray-800 whitespace-pre-wrap">{m.message}</p>
                        <p className={`mt-1 text-[10px] ${emailOk ? 'text-emerald-600' : 'text-gray-400'}`}>
                          {emailOk ? '✓ E-pos gestuur' : (m.email_status || 'Geen e-pos')}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <Textarea
              data-testid="message-writer-textarea"
              placeholder="Bv: Hallo! Kan jy asseblief Hoofstuk 3 weer kontroleer — daar is &apos;n leë bladsy in die voorbeeld."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              rows={5}
              maxLength={2000}
              autoFocus
            />
            <div className="flex justify-between items-center text-xs text-gray-500">
              <span>{messageText.length} / 2000 karakters</span>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => { setMessageBook(null); setMessageText(''); setMessageHistory([]); }}
                disabled={sendingMessage}
              >
                Kanselleer
              </Button>
              <Button
                onClick={handleSendMessage}
                disabled={sendingMessage || !messageText.trim()}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="message-writer-send-btn"
              >
                {sendingMessage ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Stuur...</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Stuur boodskap</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookManagement;